/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2022. All Rights Reserved.
 #*
 #*===================================================================
*/

import { createAction, props } from '@ngrx/store';

import {
  AgingReport,
  AgingReportSearch,
  BillCollectionItem,
  BillCollectionReport,
  BillCollectionSearch,
  ClientDocument,
  Invoice,
  InvoiceFlag,
  InvoiceItem,
  InvoicePaymentInfo,
} from '@core/models';
import { AgingReportPeriod } from '@core/enums';

export const loadAgingReport = createAction(
  '[Aging Reports] Load Aging Report',
  props<{ search: AgingReportSearch }>()
);
export const loadAgingReportSuccess = createAction(
  '[Aging Reports] Load Aging Report Success',
  props<{ report: AgingReport }>()
);
export const loadAgingReportFailure = createAction(
  '[Aging Reports] Load Aging Report Failure',
  props<{ errorMessage: string }>()
);

export const loadAgingInvoices = createAction(
  '[Aging Reports] Load Aging Invoices',
  props<{ clientId: number; invoicesPeriod?: AgingReportPeriod }>()
);
export const loadAgingInvoicesSuccess = createAction(
  '[Aging Reports] Load Aging Invoices Success',
  props<{ invoices: BillCollectionItem[] }>()
);
export const loadAgingInvoicesFailure = createAction(
  '[Aging Reports] Load Aging Invoices Failure',
  props<{ errorMessage: string }>()
);

export const loadBillCollection = createAction(
  '[Bill Collection] Load Bill Collection',
  props<{ search: BillCollectionSearch }>()
);
export const loadBillCollectionSuccess = createAction(
  '[Bill Collection] Load Bill Collection Success',
  props<{ report: BillCollectionReport }>()
);
export const loadBillCollectionFailure = createAction(
  '[Bill Collection] Load Bill Collection Failure',
  props<{ errorMessage: string }>()
);

export const flagInvoice = createAction('[Invoices Report Table] Flag Invoice', props<{ invoiceFlag: InvoiceFlag }>());
export const flagInvoiceSuccess = createAction(
  '[Invoices Report Table] Flag Invoice Success',
  props<{ invoiceFlag: InvoiceFlag }>()
);
export const flagInvoiceFailure = createAction(
  '[Invoices Report Table] Flag Invoice Failure',
  props<{ errorMessage: string }>()
);

export const updateInvoiceFlag = createAction(
  '[Invoices Report Table] Update Invoice Flag',
  props<{ invoiceFlag: InvoiceFlag }>()
);
export const updateInvoiceFlagSuccess = createAction(
  '[Invoices Report Table] Update Invoice Flag Success',
  props<{ invoiceFlag: InvoiceFlag }>()
);
export const updateInvoiceFlagFailure = createAction(
  '[Invoices Report Table] Update Invoice Flag Failure',
  props<{ errorMessage: string }>()
);

export const deleteInvoiceFlag = createAction(
  '[Invoices Report Table] Delete Invoice Flag',
  props<{ invoiceFlag: InvoiceFlag; flagedInvoicesFilter?: boolean }>()
);
export const deleteInvoiceFlagSuccess = createAction(
  '[Invoices Report Table] Delete Invoice Flag Success',
  props<{ invoiceFlag: InvoiceFlag; flagedInvoicesFilter?: boolean }>()
);
export const deleteInvoiceFlagFailure = createAction(
  '[Invoices Report Table] Delete Invoice Flag Failure',
  props<{ errorMessage: string }>()
);

export const loadInvoiceDetails = createAction('[Invoices Report Table] Load invoice Details', props<{ id: number }>());
export const loadInvoiceDetailsSuccess = createAction(
  '[Invoices Report Table] Load invoice Details Success',
  props<{ invoice: Invoice }>()
);
export const loadInvoiceDetailsFailure = createAction(
  '[Invoices Report Table] Load invoice Details Failure',
  props<{ errorMessage: string }>()
);
export const addInvoiceItem = createAction(
  '[Invoice Details] Add Invoice Items',
  props<{ invoiceItem: Partial<InvoiceItem>; document?: File }>()
);
export const addInvoiceItemSuccess = createAction(
  '[Invoice Details] Add Invoice Items Success',
  props<{ invoice: Invoice; billCollectionItem: BillCollectionItem }>()
);
export const addInvoiceItemFailure = createAction(
  '[Invoice Details] Add Invoice Items Failure',
  props<{ errorMessage: string }>()
);

export const deleteInvoiceItem = createAction('[Invoice Details] Delete Invoice Items', props<{ id: number }>());
export const deleteInvoiceItemSuccess = createAction(
  '[Invoice Details] Delete Invoice Items Success',
  props<{ invoice: Invoice; billCollectionItem: BillCollectionItem }>()
);
export const deleteInvoiceItemFailure = createAction(
  '[Invoice Details] Delete Invoice Items Failure',
  props<{ errorMessage: string }>()
);

export const loadOrderTermsDocument = createAction(
  '[Invoices Report Table] Load Order Terms Document',
  props<{ clientId: number }>()
);
export const loadOrderTermsDocumentSuccess = createAction(
  '[Invoices Report Table] Load Order Terms Document Success',
  props<{ orderTermsDocument: ClientDocument }>()
);
export const loadOrderTermsDocumentFailure = createAction(
  '[Invoices Report Table] Load Order Terms Document Failure',
  props<{ errorMessage: string }>()
);

export const setPaymentInfo = createAction(
  '[Invoices Report Table] Set Invoice Payment Info',
  props<{ invoice: Invoice; paymentInfo: InvoicePaymentInfo }>()
);
export const setPaymentInfoSuccess = createAction(
  '[Invoices Report Table] Set Invoice Payment Info Success',
  props<{ invoice: Invoice }>()
);
export const setPaymentInfoFailure = createAction(
  '[Invoices Report Table] Set Invoice Payment Info Failure',
  props<{ errorMessage: string }>()
);

export const exportBillCollection = createAction('[Bill Collection] Export Bill Collection');
export const exportAgingReport = createAction('[Aging Report] Export Aging Report');
export const openAddEditInvoiceItemDialog = createAction(
  '[Invoice Details] Open Add/Edit Invoice Item Dialog',
  props<{ invoice: Invoice; invoiceItem: InvoiceItem }>()
);
